import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { responsiveTypography, responsiveSpacing } from '../../themes/responsive';
import { experimentColors } from '../../themes/experimentColors';
import ExperimentSection from './ExperimentSection';
import DateEditPopover from './DateEditPopover';
import StartEarlyModal from './StartEarlyModal';
import DateStatusIndicator from './DateStatusIndicator';
import { API_BASE_URL } from '../../config/api.config';

/**
 * Shared ExperimentCard component
 * Clean, simple API for rendering experiment cards consistently across the app
 */
const ExperimentCard = ({
  // Data
  item,
  productKey,
  machineKey,
  productData,
  
  // Customization
  titleComponent,
  focusedItemId = null,
  summarized = false,
  headerControls = null, // New prop for header controls
  
  // Event handlers
  onAccept,
  onReject,
  onComboSelect, // New prop for combo selection
  onStartEarly,
  onStatusChange, // New generic status change handler
  
  // State
  experimentStates,
  onExperimentStateChange,
  
  // Menu state
  anchorEl,
  selectedProductKey,
  onMenuClose,
  
  // Shared functionality
  discountManagement,
  renderActionButtons,
  dataAdapter,
  
  // Selection props
  isSelected = false,
  selectionMode = false,
  
  // Utils
  t,
  clientId
}) => {
  // Get current language for date formatting
  const { i18n } = useTranslation();
  
  // Internal state management for self-contained functionality
  const [internalSelectedCombo, setInternalSelectedCombo] = React.useState(() => {
    // Initialize from experiment data
    const initialCombo = item?.experiment?.selectedCombo ||
                        (item?.experiment?.comboPrices?.[0] && {
                          name: item.experiment.comboPrices[0].candidateName,
                          price: item.experiment.comboPrices[0].totalWithDiscount.toFixed(2)
                        });
    return initialCombo;
  });
  
  const [internalLoadingState, setInternalLoadingState] = React.useState(false);
  
  // Use internal state (card is now self-contained)
  const effectiveSelectedCombo = internalSelectedCombo;
  const effectiveLoadingState = internalLoadingState;
  
  // State for date editing
  const [dateEditAnchorEl, setDateEditAnchorEl] = React.useState(null);
  const [dateEditLoading, setDateEditLoading] = React.useState(false);
  
  // State for start early modal
  const [startEarlyModalOpen, setStartEarlyModalOpen] = React.useState(false);
  const [startEarlyLoading, setStartEarlyLoading] = React.useState(false);
  
  // Enrich item with shared experiment state
  const enrichedSlot = {
    ...item,
    experiment: experimentStates[machineKey] || dataAdapter.getExperiment(item)
  };

  // Internal combo selection handler
  const handleInternalComboSelect = React.useCallback((productKey, combo) => {
    // Update internal state
    setInternalSelectedCombo(combo);
    
    // If external handler provided, call it too
    if (onComboSelect) {
      onComboSelect(productKey, combo);
    } else if (discountManagement?.handleComboSelect) {
      // Use discount management to track pending combo change
      discountManagement.handleComboSelect(productKey, combo, item);
    }
  }, [productKey, item, onComboSelect, discountManagement]);

  // Create wrapper functions for discount management
  const handleDiscountInputChange = (productKey, value) => {
    discountManagement.handleDiscountInputChange(productKey, value, item, productData, effectiveSelectedCombo);
  };
  
  const handleDiscountReset = (productKey) => {
    discountManagement.handleDiscountReset(productKey, item, productData, effectiveSelectedCombo);
  };
  
  const handleSaveChanges = (productKey) => {
    setInternalLoadingState(true);
    discountManagement.handleSaveChanges(productKey, item, productData, effectiveSelectedCombo)
      .finally(() => setInternalLoadingState(false));
  };

  // Start early handlers
  const handleStartEarlyClose = () => {
    setStartEarlyModalOpen(false);
    setStartEarlyLoading(false);
  };

  const handleStartEarlyConfirm = async () => {
    setStartEarlyLoading(true);
    try {
      await onStartEarly(productKey);
      handleStartEarlyClose();
    } catch (error) {
      setStartEarlyLoading(false);
    }
  };

  // Helper function to format experiment dates
  const formatExperimentDate = (date) => {
    return new Date(date).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { 
      day: 'numeric', 
      month: 'long' 
    });
  };

  // Date editing handlers
  const handleDateEditClick = (event) => {
    setDateEditAnchorEl(event.currentTarget);
  };

  const handleDateEditClose = () => {
    setDateEditAnchorEl(null);
    setDateEditLoading(false);
  };

  const handleDateSave = async (dates) => {
    setDateEditLoading(true);
    try {
      const experiment = enrichedSlot.experiment;
      if (!experiment?.id) return;

      // Use the new dates endpoint
      const token = localStorage.getItem('access_token');
      const response = await fetch(
        `${API_BASE_URL}/experiments/${experiment.id}/dates?clientId=${clientId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            dateFrom: dates.beginDate,
            dateTo: dates.endDate
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update dates');
      }

      // Trigger data refresh
      window.dispatchEvent(new CustomEvent('experimentMutation', { 
        detail: { 
          action: 'updateDates', 
          experimentId: experiment.id
        }
      }));

      handleDateEditClose();
    } catch (error) {
      setDateEditLoading(false);
    }
  };

  return (
    <Box
      key={`${productKey}-${enrichedSlot.experiment?.status || 'none'}`}
      data-product-key={productKey}
      sx={{
        background: `linear-gradient(135deg, ${colors.background.surface} 0%, ${colors.background.subtle} 100%)`,
        border: isSelected 
          ? `2px solid ${colors.action.primary}` 
          : focusedItemId === item.id 
            ? `2px solid ${colors.action.primary}` 
            : `1px solid ${colors.border.dark}`,
        borderRadius: '12px',
        p: responsiveSpacing.sm,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 250ms ease-out',
        cursor: 'pointer',
        backgroundColor: isSelected 
          ? 'rgba(24, 144, 255, 0.05)' 
          : focusedItemId === item.id 
            ? 'rgba(24, 144, 255, 0.02)' 
            : 'transparent',
        boxShadow: isSelected ? `0 4px 15px ${colors.shadow.soft}` : 'none',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 15px ${colors.shadow.soft}`
        },
        '&.status-change-animation': {
          transform: 'scale(1.02)',
          transition: 'transform 0.3s ease-out'
        }
      }}
    >
      {/* Header Controls - Optional controls area at the very top */}
      {headerControls && (
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: '8px', // Small space between header controls and title
          mx: '-2px', // Compensate for control padding to align with content
        }}>
          {headerControls}
        </Box>
      )}
      
      {/* Product Header */}
      <Box sx={{ 
        mb: '8px'
      }}>
        {/* Title Component */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: responsiveSpacing.xs }}>
          {/* Custom Title Component */}
          <Box sx={{ flex: 1 }}>
            {titleComponent}
          </Box>
        </Box>
      </Box>


      {/* Experiment Section */}
      <Box>
        <ExperimentSection
          item={enrichedSlot}
          productKey={productKey}
        isEditable={enrichedSlot.experiment?.status !== "running" && enrichedSlot.experiment?.status !== "finished"}
        selectedCombo={effectiveSelectedCombo || productData[productKey]?.candidates[0]}
        experiment={dataAdapter.getExperiment(enrichedSlot)}
        getEffectiveDiscount={discountManagement.getEffectiveDiscount}
        calculateDiscountedPrice={discountManagement.calculateDiscountedPrice}
        apiResponseOverrides={discountManagement.apiResponseOverrides}
        customDiscounts={discountManagement.customDiscounts}
        discountInputs={discountManagement.discountInputs}
        setDiscountInputs={discountManagement.setDiscountInputs}
        pendingSaves={discountManagement.pendingSaves}
        updatedStates={discountManagement.updatedStates}
        onComboSelect={handleInternalComboSelect}
        onDiscountInputChange={handleDiscountInputChange}
        onDiscountReset={handleDiscountReset}
        onSaveChanges={handleSaveChanges}
        productData={productData}
        t={t}
        productName={dataAdapter.getProductName(enrichedSlot)}
        // Product selection props
        showProductSelection={discountManagement.showProductSelection}
        onCustomProductSelect={discountManagement.handleCustomProductSelect}
        onCancelProductSelection={discountManagement.handleCancelProductSelection}
        onSelectNewProduct={discountManagement.handleSelectNewProduct}
        clientId={clientId}
      />
      </Box>


      {/* Duration Section */}
      <Box sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        borderRadius: '8px',
        p: '6px',
        mb: '6px',
        border: `1px solid ${colors.border.light}`
      }}>
        {/* Date Status Indicator - At the top of date box */}
        {enrichedSlot.experiment && (
          <DateStatusIndicator 
            experiment={enrichedSlot.experiment} 
            status={enrichedSlot.experiment.status} 
          />
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography sx={{
            ...responsiveTypography.small,
            color: colors.text.primary,
            fontWeight: 400,
            lineHeight: 1,
            textAlign: 'left',
            flex: 1
          }}>
            {enrichedSlot.experiment && enrichedSlot.experiment.dateFrom && enrichedSlot.experiment.dateTo ? (
              `${t('from')} ${new Date(enrichedSlot.experiment.dateFrom).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })} ${t('to')} ${new Date(enrichedSlot.experiment.dateTo).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { day: 'numeric', month: 'long' })}`
            ) : (
              `${t('from')} ${productData[productKey]?.startDate} ${t('to')} ${productData[productKey]?.endDate}`
            )}
          </Typography>
          
          {/* Edit Icon - only show for editable experiments */}
          {(enrichedSlot.experiment?.status !== "running" && enrichedSlot.experiment?.status !== "finished") && (
            <IconButton
              onClick={handleDateEditClick}
              size="small"
              sx={{
                p: '2px',
                ml: '4px',
                '&:hover': {
                  backgroundColor: colors.background.subtle
                }
              }}
            >
              <Edit sx={{ 
                fontSize: '12px', 
                color: colors.text.secondary 
              }} />
            </IconButton>
          )}
        </Box>
      </Box>



      {/* Loading Indicator */}
      {effectiveLoadingState && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '12px',
          zIndex: 10
        }}>
          <Typography sx={{
            ...responsiveTypography.small,
            color: colors.text.secondary
          }}>
            {t('loading')}
          </Typography>
        </Box>
      )}

      {enrichedSlot.experiment?.appliedPricing && (
        <Box sx={{ mt: '8px', p: '7px', borderRadius: '8px', backgroundColor: 'rgba(40,25,62,0.05)', border: `1px solid ${colors.border.light}` }}>
          <Typography sx={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: colors.text.secondary, mb: '4px' }}>
            {t('appliedPrices') || 'Precios aplicados'}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 10px' }}>
            {[
              [t('appliedPriceCash'), 'cash'], [t('appliedPriceCard'), 'card'], [t('appliedPriceBank'), 'bank'], [t('appliedPriceMobile'), 'mobile']
            ].map(([label, key]) => (
              <Typography key={key} sx={{ fontSize: '11px', color: colors.text.primary }}>
                {label}: €{Number(enrichedSlot.experiment.appliedPricing[key] || 0).toFixed(2)}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* Date Edit Popover */}
      <DateEditPopover
        open={Boolean(dateEditAnchorEl)}
        anchorEl={dateEditAnchorEl}
        onClose={handleDateEditClose}
        currentStartDate={enrichedSlot.experiment?.dateFrom}
        currentEndDate={enrichedSlot.experiment?.dateTo}
        onSave={handleDateSave}
        loading={dateEditLoading}
        t={t}
      />

      {/* Start Early Modal */}
      <StartEarlyModal
        open={startEarlyModalOpen}
        onClose={handleStartEarlyClose}
        onConfirm={handleStartEarlyConfirm}
        currentStartDate={enrichedSlot.experiment?.dateFrom}
        endDate={enrichedSlot.experiment?.dateTo}
        loading={startEarlyLoading}
        t={t}
      />
    </Box>
  );
};

export default ExperimentCard;
